import type { AssignedUser, AssignedUserPayload } from './types';

// Helper function to get user initials from name (2 letters max)
export function getInitials(name: string): string {
  if (!name) return '??';

  // If it's an email, use first 2 chars of local part
  if (name.includes('@')) {
    const localPart = name.split('@')[0];
    return localPart.slice(0, 2).toUpperCase();
  }

  // For names like "Fred Krueger", get first letter of each word
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }

  // Single word name - use first 2 characters
  return name.slice(0, 2).toUpperCase();
}

// Helper function to get consistent color based on position in sorted members
export function getUserColor(userId: string, conversationMembers: any[]): string {
  const colors = [
    'var(--color-assignee-1)',  // Soft Blue
    'var(--color-assignee-2)',  // Soft Lavender
    'var(--color-assignee-3)',  // Soft Peach
    'var(--color-assignee-4)',  // Soft Mint
    'var(--color-assignee-5)',  // Soft Rose
  ];

  const sortedMembers = [...conversationMembers].sort((a, b) => (a.id || '').localeCompare(b.id || ''));
  const index = sortedMembers.findIndex((m) => m.id === userId);

  return index >= 0 ? colors[index % colors.length] : colors[0];
}

// Helper functions to handle assignedTo format
export function getAssignedUserId(assigned: string | AssignedUser): string {
  return typeof assigned === 'string' ? assigned : assigned.id;
}

export function getAssignedUserName(assigned: string | AssignedUser, conversationMembers: any[]): string {
  const userId = typeof assigned === 'string' ? assigned : assigned.id;

  const member = conversationMembers.find((m: any) => m.id === userId);
  if (member?.name) {
    return member.name;
  }

  if (typeof assigned !== 'string' && assigned.name) {
    return assigned.name;
  }

  return member?.email || 'Unknown';
}

export function normalizeAssignedToPayload(
  assignedTo: string[] | null | undefined,
  conversationMembers: Array<{ id?: string; name?: string; email?: string }>,
): AssignedUserPayload[] | undefined {
  if (!assignedTo || assignedTo.length === 0) return undefined;

  const normalized = assignedTo
    .map((value) => (typeof value === 'string' ? value.trim() : ''))
    .filter(Boolean)
    .map((token) => {
      const lower = token.toLowerCase();
      const member = conversationMembers.find((m) => (
        (m.id && m.id === token) ||
        (m.email && m.email.toLowerCase() === lower) ||
        (m.name && m.name.toLowerCase() === lower)
      ));

      if (member) {
        return {
          id: member.id ?? token,
          name: member.name ?? member.email ?? token,
        };
      }

      return { id: token, name: token };
    });

  return normalized.length > 0 ? normalized : undefined;
}

// Check if due date is overdue or soon
export function getDueDateStatus(dueAt?: string): 'overdue' | 'today' | 'soon' | 'normal' | null {
  if (!dueAt) return null;
  const due = new Date(dueAt);
  due.setHours(23, 59, 59, 999);
  const now = new Date();
  const today = new Date();
  today.setHours(23, 59, 59, 999);
  const threeDays = new Date(today);
  threeDays.setDate(threeDays.getDate() + 3);

  if (due < now) return 'overdue';
  if (due <= today) return 'today';
  if (due <= threeDays) return 'soon';
  return 'normal';
}

// Get due date color based on urgency
export function getDueDateColor(dateStr: string): string {
  const datePart = dateStr.split('T')[0];
  const [year, month, day] = datePart.split('-').map(Number);
  const dueDate = new Date(year, month - 1, day);

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const diffDays = Math.round((dueDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

  if (diffDays < 0) return 'var(--color-danger)';
  if (diffDays === 0) return 'var(--color-primary)';
  if (diffDays === 1) return 'var(--color-warning)';
  return 'var(--color-text-muted)';
}

// Helper to create ISO date string for a local date
export function toLocalISODate(date: Date): string {
  const year = date.getFullYear();
  const month = date.getMonth();
  const day = date.getDate();
  const noonLocal = new Date(year, month, day, 12, 0, 0);
  return noonLocal.toISOString();
}

export function normalizeImportDueAt(dueDate?: string | null): string | undefined {
  if (!dueDate) return undefined;
  const trimmed = dueDate.trim();
  if (!trimmed) return undefined;

  const dateOnlyMatch = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (dateOnlyMatch) {
    const year = Number(dateOnlyMatch[1]);
    const month = Number(dateOnlyMatch[2]) - 1;
    const day = Number(dateOnlyMatch[3]);
    return toLocalISODate(new Date(year, month, day));
  }

  const parsed = new Date(trimmed);
  if (Number.isNaN(parsed.getTime())) return undefined;
  return parsed.toISOString();
}

// Format due date - show day of week for dates within a week
export function formatDueDate(dateStr: string): string {
  const datePart = dateStr.split('T')[0];
  const [year, month, day] = datePart.split('-').map(Number);
  const dueDate = new Date(year, month - 1, day);

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const diffDays = Math.round((dueDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Tomorrow';
  if (diffDays === -1) return 'Yesterday';
  if (diffDays > 1 && diffDays <= 6) {
    return dueDate.toLocaleDateString('en-US', { weekday: 'long' });
  }
  if (diffDays >= -6 && diffDays < -1) {
    return 'Last ' + dueDate.toLocaleDateString('en-US', { weekday: 'long' });
  }
  return dueDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

// Parse date from task text
export function parseDateFromText(text: string): { taskText: string; dueDate?: string } {
  let taskText = text;
  let dueDate: string | undefined;

  if (/\btomorrow\b/i.test(text)) {
    taskText = text.replace(/\btomorrow\b/i, '').trim();
    const date = new Date();
    date.setDate(date.getDate() + 1);
    dueDate = toLocalISODate(date);
  } else if (/\btoday\b/i.test(text)) {
    taskText = text.replace(/\btoday\b/i, '').trim();
    dueDate = toLocalISODate(new Date());
  }
  else if (/\b(monday|mon)\b/i.test(text)) {
    taskText = text.replace(/\b(monday|mon)\b/i, '').trim();
    dueDate = getNextWeekday(1);
  } else if (/\b(tuesday|tue|tues)\b/i.test(text)) {
    taskText = text.replace(/\b(tuesday|tue|tues)\b/i, '').trim();
    dueDate = getNextWeekday(2);
  } else if (/\b(wednesday|wed)\b/i.test(text)) {
    taskText = text.replace(/\b(wednesday|wed)\b/i, '').trim();
    dueDate = getNextWeekday(3);
  } else if (/\b(thursday|thu|thur|thurs)\b/i.test(text)) {
    taskText = text.replace(/\b(thursday|thu|thur|thurs)\b/i, '').trim();
    dueDate = getNextWeekday(4);
  } else if (/\b(friday|fri)\b/i.test(text)) {
    taskText = text.replace(/\b(friday|fri)\b/i, '').trim();
    dueDate = getNextWeekday(5);
  } else if (/\b(saturday|sat)\b/i.test(text)) {
    taskText = text.replace(/\b(saturday|sat)\b/i, '').trim();
    dueDate = getNextWeekday(6);
  } else if (/\b(sunday|sun)\b/i.test(text)) {
    taskText = text.replace(/\b(sunday|sun)\b/i, '').trim();
    dueDate = getNextWeekday(0);
  }
  else {
    const daysMatch = text.match(/\bin (\d+) days?\b/i);
    if (daysMatch) {
      taskText = text.replace(/\bin (\d+) days?\b/i, '').trim();
      const days = parseInt(daysMatch[1]);
      const date = new Date();
      date.setDate(date.getDate() + days);
      dueDate = toLocalISODate(date);
    } else {
      const monthNames: Record<string, number> = {
        jan: 0, january: 0, feb: 1, february: 1, mar: 2, march: 2,
        apr: 3, april: 3, may: 4, jun: 5, june: 5,
        jul: 6, july: 6, aug: 7, august: 7, sep: 8, sept: 8, september: 8,
        oct: 9, october: 9, nov: 10, november: 10, dec: 11, december: 11
      };
      const monthDayMatch = text.match(/\b(jan|january|feb|february|mar|march|apr|april|may|jun|june|jul|july|aug|august|sep|sept|september|oct|october|nov|november|dec|december)\s+(\d{1,2})\b/i);
      if (monthDayMatch) {
        const monthName = monthDayMatch[1].toLowerCase();
        const day = parseInt(monthDayMatch[2]);
        const month = monthNames[monthName];
        taskText = text.replace(/\b(jan|january|feb|february|mar|march|apr|april|may|jun|june|jul|july|aug|august|sep|sept|september|oct|october|nov|november|dec|december)\s+(\d{1,2})\b/i, '').trim();
        const date = new Date();
        date.setMonth(month, day);
        if (date < new Date()) date.setFullYear(date.getFullYear() + 1);
        dueDate = toLocalISODate(date);
      } else {
        const dateMatch = text.match(/\b(\d{1,2})\/(\d{1,2})\b/);
        if (dateMatch) {
          taskText = text.replace(/\b(\d{1,2})\/(\d{1,2})\b/, '').trim();
          const month = parseInt(dateMatch[1]) - 1;
          const day = parseInt(dateMatch[2]);
          const date = new Date();
          date.setMonth(month, day);
          if (date < new Date()) date.setFullYear(date.getFullYear() + 1);
          dueDate = toLocalISODate(date);
        }
      }
    }
  }

  return { taskText, dueDate };
}

function getNextWeekday(targetDay: number): string {
  const date = new Date();
  const currentDay = date.getDay();
  let daysToAdd = targetDay - currentDay;
  if (daysToAdd <= 0) daysToAdd += 7;
  date.setDate(date.getDate() + daysToAdd);
  return toLocalISODate(date);
}

// Migrate single label to multi-label format
export function migrateLabels(task: { label?: string; labelColor?: string; labels?: Array<{ name: string; color: string }> }): Array<{ name: string; color: string }> {
  if (task.labels && task.labels.length > 0) return task.labels;
  if (task.label && task.labelColor) {
    return [{ name: task.label, color: task.labelColor }];
  }
  return [];
}

// A task is archived if ANY archive signal is set. Two write shapes exist in
// the wild: the real archived_at column (status flow / current API) and the
// legacy shadow attribute (pre-fix attribute writes, flattened into reads) —
// plus the historical status==='archived' convention.
export function isTaskArchived(task: { archived?: unknown; archived_at?: unknown; archivedAt?: unknown; status?: string } | null | undefined): boolean {
  return Boolean(task?.archived || task?.archived_at || task?.archivedAt || task?.status === 'archived');
}
